import React, { useState } from 'react'
import {
  Rss, Loader2, Plus, RotateCw, Trash2, ExternalLink, Mic, FileText,
  FolderInput, CheckCircle2,
} from 'lucide-react'
import {
  useFeeds, useFeedItems, useAddFeed, useDeleteFeed, useRefreshFeed, useImportFeedItem,
} from '../hooks/useFeeds'

// F1 — Feeds mode in Sources: subscribe RSS/Atom, browse items, import into the
// shared Sources project. Audio episodes run the durable transcribe→index saga;
// text articles index directly with provenance (origin 'rss').

const fmtDate = (s) => {
  if (!s) return ''
  const d = new Date(s)
  return isNaN(d) ? String(s).slice(0, 16) : d.toLocaleDateString()
}

export default function FeedsPanel({ project }) {
  const feedsQ = useFeeds()
  const [feedId, setFeedId] = useState(null)
  const feeds = feedsQ.data?.feeds ?? []
  const selected = feeds.find((f) => f.id === feedId) ?? null
  const addFeed = useAddFeed()
  const delFeed = useDeleteFeed()
  const refresh = useRefreshFeed()
  const [url, setUrl] = useState('')

  const subscribe = () =>
    url.trim() &&
    addFeed.mutate(
      { url: url.trim() },
      { onSuccess: (r) => { setUrl(''); if (r?.feed?.id) setFeedId(r.feed.id) } }
    )

  return (
    <div className="space-y-4">
      {/* subscribe */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Rss className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft dark:text-mist/50" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && subscribe()}
            placeholder="Paste an RSS/Atom feed URL (podcast or blog)…"
            className="w-full h-10 rounded-lg pl-9 pr-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          />
        </div>
        <button
          onClick={subscribe}
          disabled={addFeed.isPending || !url.trim()}
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {addFeed.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Subscribe
        </button>
      </div>
      {addFeed.isError && <p className="text-sm text-red-600">{addFeed.error?.message}</p>}

      {/* subscriptions */}
      {feeds.length === 0 && !feedsQ.isLoading && (
        <div className="rounded-xl border border-dashed border-parchment-border p-6 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
          <Rss className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Subscribe to a podcast or blog feed to pull its items into your research.
        </div>
      )}
      {feeds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {feeds.map((f) => (
            <button
              key={f.id}
              onClick={() => setFeedId(f.id === feedId ? null : f.id)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition ${
                f.id === feedId
                  ? 'border-gold/60 bg-gold/10 text-gold-deep dark:text-gold'
                  : 'border-parchment-border text-ink-soft hover:border-gold dark:border-midnight-600 dark:text-mist/60'
              }`}
            >
              {f.kind === 'audio' ? <Mic className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
              <span className="max-w-[220px] truncate">{f.title}</span>
              {f.new_items > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-spark/15 text-spark-600 dark:text-spark">
                  {f.new_items} new
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* selected feed */}
      {selected && (
        <FeedItems
          feed={selected}
          project={project}
          onRefresh={() => refresh.mutate(selected.id)}
          refreshing={refresh.isPending}
          onDelete={() => { delFeed.mutate(selected.id); setFeedId(null) }}
        />
      )}
    </div>
  )
}

function FeedItems({ feed, project, onRefresh, refreshing, onDelete }) {
  const itemsQ = useFeedItems(feed.id)
  const items = itemsQ.data?.items ?? []
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-ink-soft dark:text-mist/50">
        <span className="uppercase tracking-wide">{feed.kind} feed</span>
        {feed.last_checked && <span>· checked {fmtDate(feed.last_checked)}</span>}
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-parchment-border hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:hover:text-gold disabled:opacity-50"
        >
          <RotateCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> refresh
        </button>
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-parchment-border hover:border-red-400 hover:text-red-600 dark:border-midnight-600"
        >
          <Trash2 className="w-3 h-3" /> unsubscribe
        </button>
        {!project && <span className="ml-auto italic">pick a project above to enable Import</span>}
      </div>

      {itemsQ.isLoading && (
        <div className="flex items-center gap-2 text-sm text-ink-soft dark:text-mist/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading items…
        </div>
      )}
      {items.length > 0 && (
        <ul className="divide-y divide-parchment-border dark:divide-midnight-600 rounded-xl border border-parchment-border dark:border-midnight-600">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-3 px-4 py-2.5 bg-white/60 dark:bg-midnight-800/50 first:rounded-t-xl last:rounded-b-xl">
              {it.kind === 'audio' ? (
                <Mic className="w-4 h-4 shrink-0 text-ink-soft dark:text-mist/50" />
              ) : (
                <FileText className="w-4 h-4 shrink-0 text-ink-soft dark:text-mist/50" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ink dark:text-mist truncate" title={it.title}>{it.title}</div>
                <div className="text-xs text-ink-soft dark:text-mist/50 flex items-center gap-2">
                  {it.published && <span>{fmtDate(it.published)}</span>}
                  {it.link && (
                    <a href={it.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:text-gold-deep dark:hover:text-gold">
                      <ExternalLink className="w-3 h-3" /> source
                    </a>
                  )}
                </div>
              </div>
              <ImportItemButton item={it} project={project} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ImportItemButton({ item, project }) {
  const imp = useImportFeedItem()

  if (item.imported_at) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-gold-deep dark:text-gold"
        title={`Imported into "${item.imported_to}"${item.saga_id ? ` (saga #${item.saga_id})` : ''}`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> in {item.imported_to}
      </span>
    )
  }
  if (imp.isSuccess) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 className="w-3.5 h-3.5" />
        {imp.data?.kind === 'audio' ? 'transcribing…' : 'indexed'}
      </span>
    )
  }
  return (
    <button
      onClick={() => imp.mutate({ itemId: item.id, project })}
      disabled={!project || imp.isPending}
      title={
        imp.isError
          ? imp.error?.message
          : project
            ? item.kind === 'audio'
              ? `Download, transcribe and index into "${project}" (runs server-side)`
              : `Index the article into "${project}"`
            : 'Pick a project above first'
      }
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border transition disabled:opacity-40 ${
        imp.isError
          ? 'border-red-300 text-red-600'
          : 'border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold'
      }`}
    >
      {imp.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
      {imp.isError ? 'retry' : project ? `Import → ${project}` : 'Import'}
    </button>
  )
}
